import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

import { useTranslation } from "react-i18next";


const LineChart = ({ data1, color, keyid, refreshKey, higherLimit, lowerLimit }) => {

  const [t] = useTranslation('common');

  const chartRef = useRef();
  const [keyid1, setKeyid1] = useState('graph' + keyid);
  const [dataUpdate, setDataUpdate] = useState(0); // Add state variable for data update
  const [data, setData] = useState(data1);

  useEffect(() => {
    if (d3.select(`.${keyid1}`).selectAll('*')) {
      d3.select(`.${keyid1}`).selectAll('*').remove();
    }
    drawChart();
  }, [data, dataUpdate, refreshKey])

  const updateChartOnResize = () => {
    // Increment the dataUpdate state to trigger the chart update
    setDataUpdate((prev) => prev + 1);
  };

  const drawChart = () => {

    let vehData = data.trips;
    let stopsData = data.plannedStops;

    // Unique stops for x scale
    let unique_sdt = [...new Set(stopsData.map(i => i.x))];

    let stops = stopsData.map((i) => {
      return {
        type: 'stop',
        position: i.x,
        name: i.name
      }
    })

    // J'ai reconstruit l'array pcq fallait avoir un champ nommé pareil pour
    // stops et véhicules (champ position) - sinon j'aurais pu réutiliser directement
    // vehData
    let vehicles = vehData.map((i) => {
      return {
        type: 'vehicle',
        position: i.x,
        vehicle_id: i.vehicle_id,
        speed: i.speed !== 'N-D' ? parseFloat(i.speed).toFixed(0) : 'N-D',
        route_id: i.route_id,
        trip_id: i.trip_id,
        delay: i.delay,
        occupancy_status: i.occupancy_status
      }
    });

    // Tick labels (text)
    let tickLabels = stopsData.map((e) => { return e.name });
    let tickLabelsFormat = [tickLabels[0], tickLabels[tickLabels.length - 1]];

    // Chart data : first value is drawn first, so second value is drawn over 1st value of this array

    let chartData = [
      { name: 'stops', values: stops },
      { name: 'vehicles', values: vehicles }
    ]

    const margins = { top: 10, right: 25, bottom: 10, left: 25 };

    //const width = 900;
    const width = chartRef.current ? chartRef.current.parentElement.offsetWidth : '';
    //const width = chartRef.current.parentElement.offsetWidth;
    const height = 100;
    const innerWidth = width - margins.left - margins.right;
    const innerHeight = height - margins.top - margins.bottom;

    //X scale
    let xScale = d3.scaleLinear()
      .domain([d3.min(unique_sdt), d3.max(unique_sdt)])
      .range([0, innerWidth])


    //Setting chart width and adjusting for margins
    const chart = d3.select(`.${keyid1}`)
      .attr('width', innerWidth + margins.right + margins.left)
      .attr('height', height + margins.top + margins.bottom)
      .append('g')
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
    //.attr('id', 'chartid')


    // Chart background and outline
    chart.append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr('transform', 'translate(' + -margins.left + ',' + -margins.top + ')')
      .attr("fill", "white")
      .attr("stroke", "#dee2e6")
      .attr("rx", 6)
      .attr("ry", 6)

    //Create tooltip
    const tooltip = d3.select('.container').append('div')
      .attr('class', 'tooltipline')
      .html('Tooltip')
      .attr('id', 'tooltipid' + keyid)


    // Format lines for planned series
    const line = d3.line()
      .x(function (d) {
        return xScale(d.position);
      })

    // Add lines for planned series
    chart.selectAll("myLines")
      .data(chartData)
      .join("path")
      .attr("d", d => line(d.values))
      .attr("stroke", "black")
      .attr('transform', 'translate(10,' + 0 + ')')
      .style("stroke-width", 1)
      .style("fill", "none")


    //Append x axis
    chart.append('g')
      .attr('transform', 'translate(0,' + '60)')
      .call(d3.axisBottom(xScale)
        .tickValues([xScale.domain()[0], xScale.domain()[1]])
        .tickFormat((d, i) => {
          if (width > 999) {
            return tickLabelsFormat[i]
          }
        })
      )
      .selectAll("text")
      .style("text-anchor", (d, i) => i === 0 ? "start" : "end") // Align the first tick to the start (left), and the last tick to the end (right)
      .attr("font-family", "Roboto")
      .style("font-size", "15px")
      .attr('class', 'x-axis')

    // Add dots
    chart.selectAll('myDots')
      .data(chartData)
      .join('g')
      .style('fill', (d) => {
        if (d.name == 'stops') {
          return 'black'
        } else if (d.values[0].delay > higherLimit) { return "#FFC300" }
        else if (d.values[0].delay < lowerLimit) { return "#ff0000" }
        else if (d.values[0].delay >= lowerLimit && d.values[0].delay <= higherLimit) { return "#29c521" }
      })
      .style('stroke', 'none')
      .selectAll('myPoints')
      .data(d => d.values)
      .join('circle')
      .attr("cy", 60)
      .attr("cx", function (d) {
        return xScale(d.position);
      })
      .style('opacity', 1)
      .attr("r", function (d) {
        if (d.type == 'stop') {
          return 2
        } else { return 5 }
      })
      .on('mouseover', d => {
        if (d.target.__data__.type == 'stop') {
          d3.select(d.target).attr("r", 5)
          chart.mouse = d3.pointer(d);
          chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
          tooltip.html(t("liveroutes.tooltip.stop") + d.srcElement.__data__.name + '<br/>' +
            'Distance (km) : ' + (parseFloat(d.srcElement.__data__.position) / 1000).toFixed(1))
            .style('opacity', 1)
            .style('left', chart.mouse2[0] - 75 + 'px')
            .style('top', chart.mouse2[1] - 90 + 'px')
        } else {
          d3.select(d.target).attr("r", 8)
          chart.mouse = d3.pointer(d);
          chart.mouse2 = d3.pointer(d, d3.select(chart.chartLocation));
          tooltip.html(t("liveroutes.tooltip.vehicle") + d.srcElement.__data__.vehicle_id + '<br/>' +
            'Distance (km) : ' + (parseFloat(d.srcElement.__data__.position) / 1000).toFixed(1) + '<br/>' +
            t("liveroutes.tooltip.speed") + d.srcElement.__data__.speed + '<br/>' +
            t("liveroutes.tooltip.delay") + parseFloat(d.srcElement.__data__.delay/60).toFixed(0) + " min" + '<br/>' +
            t("liveroutes.tooltip.load") + d.srcElement.__data__.occupancy_status)
            .style('opacity', 1)
            .style('left', chart.mouse2[0] - 75 + 'px')
            .style('top', chart.mouse2[1] - 150 + 'px')
        }
      }).on('mouseout', (d) => {
        let tooltips = document.querySelectorAll(".tooltipline")
        tooltips.forEach(e => {
          e.style.opacity = 0;
        });
        if (d.target && d.target.__data__.type == 'stop') {
          d3.select(d.target).attr("r", 2)
          tooltip.style('opacity', 0)
            .style('left', '0px');
        } else if (d.target && d.target.__data__.type == 'vehicle') {
          d3.select(d.target).attr("r", 5)
          tooltip.style('opacity', 0)
            .style('left', '0px');
        }
      })
      .on("mouseleave", (d) => {
        let tooltips = document.querySelectorAll(".tooltipline")
        tooltips.forEach(e => {
          e.style.opacity = 0;
        });
        if (d.target && d.target.__data__.type == 'stop') {
          d3.select(d.target).attr("r", 2)
          tooltip.style('opacity', 0)
            .style('left', '0px');
        } else if (d.target && d.target.__data__.type == 'vehicle') {
          d3.select(d.target).attr("r", 5)
          tooltip.style('opacity', 0)
            .style('left', '0px');
        }
      })

    //Dot labels
    chart.selectAll('dotlabels')
      .data(chartData)
      .join('g')
      .selectAll('myPoints')
      .data(d => d.values)
      .join('text')
      .text(function (d) {
        if (d.type == 'vehicle') {
          return d.vehicle_id;
        }
      })
      .attr("y", function (d) {
        if (d.type == 'vehicle') {
          return 50;
        }
      })
      .attr("x", function (d) {
        if (d.type == 'vehicle') {
          return xScale(d.position);
        }
      })
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")


    //Append chart title
    chart.append("text")
      .attr("x", (innerWidth / 2))
      .attr("y", 0 - (margins.top / 2) + 15)
      .attr("text-anchor", "middle")
      .attr("font-family", "Roboto")
      .style("font-size", "16px")
    //.text('Ligne ' + data.route + ' | Direction : ' + data.direction);


    // Call the updateChartOnResize function when the window is resized
    d3.select(window).on('resize.' + keyid1, updateChartOnResize);

    // Cleanup the window resize listener on component unmount
    return () => {
      d3.select(window).on('resize.' + keyid1, null);
    };

  };


  return <svg
    className={keyid1}
    ref={chartRef}></svg>;


};

export default LineChart;